import { motion, useInView, animate } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import {
    ORDER_ONLINE_URL,
    DELIVERY_URL,
    ORDER_STEPS,
    MONOGRAM_URL,
} from "../lib/data";

// "Kitchen order ticket" themed step cards: 3 paper tickets pinned up at
// slight rotations, each with a perforated top edge, an order # header,
// a huge counted step number, a typed-style title, a dotted tear-line,
// and the brand stamp at the bottom. Cards lift to upright on hover with a
// shadow bloom. Connecting "→" arrows fade in between the tickets.
// Mobile keeps a clean stagger so it stays readable.

const TICKET_META = [
    { label: "TAP", order: "ORDER #001", rotate: -2.2, delay: 0 },
    { label: "BUILD", order: "ORDER #002", rotate: 1.4, delay: 0.15 },
    { label: "PAY", order: "ORDER #003", rotate: -1.1, delay: 0.3 },
];

export default function HowToOrder() {
    return (
        <section
            data-testid="how-to-order-section"
            className="relative py-20 md:py-32 bg-[#FAF8F4] overflow-hidden"
        >
            {/* Subtle background grid texture (desktop) */}
            <div
                aria-hidden="true"
                className="hidden md:block absolute inset-0 pointer-events-none opacity-[0.04]"
                style={{
                    backgroundImage:
                        "radial-gradient(circle at 1px 1px, #121212 1px, transparent 0)",
                    backgroundSize: "28px 28px",
                }}
            />

            <div className="relative z-10 mx-auto max-w-[1280px] px-6 md:px-10 lg:px-14">
                {/* Heading */}
                <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
                    <motion.p
                        initial={{ opacity: 0, y: 8 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.5 }}
                        transition={{ duration: 0.6 }}
                        className="overline mb-5"
                    >
                        How To Order
                    </motion.p>
                    <h2 className="font-display uppercase text-[#121212] text-[clamp(2.4rem,6vw,5.5rem)] leading-[0.92]">
                        <AnimatedLine text="Three steps," />
                        <br />
                        <AnimatedLine
                            text="no fuss"
                            delay={0.25}
                            trailing={<span className="text-[#EB4A18]">.</span>}
                        />
                    </h2>
                </div>

                {/* DESKTOP — 3 kitchen order tickets side by side */}
                <div className="hidden md:flex relative items-start justify-center gap-4 lg:gap-6">
                    {ORDER_STEPS.map((s, idx) => (
                        <div
                            key={s.n}
                            className="relative flex items-start"
                        >
                            <TicketCard
                                step={s}
                                meta={TICKET_META[idx]}
                                idx={idx}
                            />
                            {idx < ORDER_STEPS.length - 1 && (
                                <ArrowConnector
                                    delay={0.6 + idx * 0.2}
                                    direction="horizontal"
                                />
                            )}
                        </div>
                    ))}
                </div>

                {/* MOBILE — same kitchen tickets stacked vertically */}
                <div className="md:hidden flex flex-col items-center gap-3 px-2">
                    {ORDER_STEPS.map((s, idx) => (
                        <div
                            key={s.n}
                            className="relative w-full flex flex-col items-center"
                        >
                            <TicketCard
                                step={s}
                                meta={TICKET_META[idx]}
                                idx={idx}
                                mobile
                            />
                            {idx < ORDER_STEPS.length - 1 && (
                                <ArrowConnector
                                    delay={0.4 + idx * 0.2}
                                    direction="vertical"
                                />
                            )}
                        </div>
                    ))}
                </div>

                {/* CTAs */}
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="mt-16 md:mt-24 flex flex-col items-center text-center"
                >
                    <div className="flex flex-wrap items-center justify-center gap-4">
                        <a
                            href={ORDER_ONLINE_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="how-to-order-online-btn"
                            className="btn-primary text-base px-10 py-5"
                        >
                            Order Pickup
                            <span aria-hidden="true">→</span>
                        </a>
                        <a
                            href={DELIVERY_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="how-to-delivery-btn"
                            className="btn-secondary text-base px-10 py-5"
                        >
                            Get Delivery
                            <span aria-hidden="true">→</span>
                        </a>
                    </div>
                    <p className="mt-5 overline text-[#6B6B6B]">
                        Pickup via Chewzie · Delivery via Uber Eats
                    </p>
                </motion.div>
            </div>
        </section>
    );
}

// Desktop ticket card — scalloped top edge, tape label, big counted number,
// dotted tear line divider, brand stamp footer, slight rotation, hover lift.
function TicketCard({ step, meta, idx, mobile = false }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.5 });

    return (
        <motion.article
            ref={ref}
            data-testid={`order-step-${step.n}`}
            initial={{ opacity: 0, y: 50, rotate: meta.rotate + 6 }}
            whileInView={{ opacity: 1, y: 0, rotate: meta.rotate }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{
                duration: 0.9,
                delay: meta.delay,
                ease: [0.19, 1, 0.22, 1],
            }}
            whileHover={{ y: -10, rotate: 0, scale: 1.02 }}
            style={{ transformOrigin: "center top" }}
            className={`relative group ${
                mobile
                    ? "w-full max-w-[340px]"
                    : "w-[280px] lg:w-[320px]"
            }`}
        >
            {/* Pin / nail — sits BELOW the tape (lower z) so the tape covers
                the top half, looking like a real pin holding the tape on */}
            <span
                aria-hidden="true"
                className="absolute top-1 left-1/2 -translate-x-1/2 z-20 w-3 h-3 rounded-full bg-[#121212] shadow-[0_2px_4px_rgba(0,0,0,0.4)]"
            />

            {/* Tape sticker on top — z-30 sits above the pin */}
            <span
                aria-hidden="true"
                className="absolute -top-3 left-1/2 -translate-x-1/2 z-30 px-4 py-1 bg-[#EB4A18] text-[#FAF8F4] text-[0.65rem] uppercase tracking-[0.28em] font-extrabold rotate-[-3deg] shadow-[0_6px_14px_-6px_rgba(235,74,24,0.6)]"
            >
                {meta.label}
            </span>

            {/* Ticket body — paper-white with scalloped top edge */}
            <div
                className="relative bg-white shadow-[0_28px_60px_-22px_rgba(0,0,0,0.35),0_8px_18px_-10px_rgba(0,0,0,0.18)] motion-safe:transition-shadow duration-500 group-hover:shadow-[0_40px_80px_-22px_rgba(0,0,0,0.45),0_12px_24px_-10px_rgba(0,0,0,0.22)]"
                style={{
                    // Scalloped top edge — half-circles cut from the top
                    maskImage:
                        "radial-gradient(circle 7px at 7px 0, transparent 99%, black 100%)",
                    WebkitMaskImage:
                        "radial-gradient(circle 7px at 7px 0, transparent 99%, black 100%)",
                    maskSize: "14px 100%",
                    WebkitMaskSize: "14px 100%",
                    maskRepeat: "repeat-x",
                    WebkitMaskRepeat: "repeat-x",
                    maskComposite: "exclude",
                }}
            >
                <div className="pt-9 pb-7 px-7">
                    {/* Order # header */}
                    <div className="flex items-center justify-between mb-5">
                        <span className="overline text-[0.6rem] text-[#6B6B6B] tracking-[0.28em]">
                            {meta.order}
                        </span>
                        <span className="inline-flex items-center gap-1 overline text-[0.6rem] text-[#6B6B6B]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#EB4A18] motion-safe:animate-pulse" />
                            Live
                        </span>
                    </div>

                    {/* Dotted divider */}
                    <DottedRule />

                    {/* Counted number */}
                    <div className="mt-5">
                        <CountedNumber
                            target={parseInt(step.n, 10)}
                            inView={inView}
                        />
                    </div>

                    {/* Title */}
                    <motion.h3
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{
                            duration: 0.7,
                            delay: meta.delay + 0.35,
                            ease: [0.19, 1, 0.22, 1],
                        }}
                        className="mt-3 font-display uppercase text-[#121212] text-[1.7rem] leading-[1] tracking-tight"
                    >
                        {step.title}
                    </motion.h3>

                    {/* Body */}
                    <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{
                            duration: 0.7,
                            delay: meta.delay + 0.5,
                            ease: [0.19, 1, 0.22, 1],
                        }}
                        className={`mt-4 text-[#4A4A4A] text-[0.95rem] leading-relaxed ${
                            mobile ? "" : "min-h-[5.5rem]"
                        }`}
                    >
                        {step.body}
                    </motion.p>

                    {/* Tear line */}
                    <div className="mt-6">
                        <DottedRule dashed />
                    </div>

                    {/* Footer stamp */}
                    <div className="mt-5 flex items-center justify-between">
                        <img
                            src={MONOGRAM_URL}
                            alt=""
                            aria-hidden="true"
                            className="h-6 w-auto opacity-50 select-none pointer-events-none"
                            style={{
                                mixBlendMode: "multiply",
                                filter: "contrast(1.2) grayscale(0.4)",
                            }}
                        />
                        <span className="overline text-[0.55rem] text-[#6B6B6B] tracking-[0.3em]">
                            The Gaffer · Albany
                        </span>
                    </div>

                    {/* "READY" stamp ink — appears on hover for the last ticket */}
                    {idx === 2 && !mobile && (
                        <span
                            aria-hidden="true"
                            className="absolute bottom-12 right-5 font-display uppercase text-[1.6rem] tracking-[0.1em] text-[#EB4A18]/85 border-[3px] border-[#EB4A18]/70 px-2 py-0.5 rotate-[-12deg] opacity-0 group-hover:opacity-100 motion-safe:transition-opacity duration-500"
                            style={{ fontFamily: "Anton, sans-serif" }}
                        >
                            Ready
                        </span>
                    )}
                </div>
            </div>
        </motion.article>
    );
}

// "→" or "↓" connector between tickets — fades + slides in
function ArrowConnector({ delay, direction = "horizontal" }) {
    const isVertical = direction === "vertical";
    return (
        <motion.span
            aria-hidden="true"
            initial={{ opacity: 0, x: isVertical ? 0 : -10, y: isVertical ? -10 : 0 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{
                duration: 0.6,
                delay,
                ease: [0.19, 1, 0.22, 1],
            }}
            className={
                isVertical
                    ? "my-2 text-[#EB4A18]/70 font-display text-4xl leading-none select-none"
                    : "mx-2 lg:mx-4 mt-[120px] text-[#EB4A18]/70 font-display text-3xl select-none"
            }
        >
            {isVertical ? "↓" : "→"}
        </motion.span>
    );
}

// A row of small dots/dashes acting as a perforation line
function DottedRule({ dashed = false }) {
    return (
        <div
            className="h-px w-full"
            style={{
                backgroundImage: dashed
                    ? "repeating-linear-gradient(to right, rgba(0,0,0,0.35) 0 6px, transparent 6px 12px)"
                    : "repeating-linear-gradient(to right, rgba(0,0,0,0.18) 0 2px, transparent 2px 6px)",
            }}
        />
    );
}

// Heading line — each word fades + slides up with a tiny rotate
function AnimatedLine({ text, delay = 0, trailing = null }) {
    const words = text.split(" ");
    return (
        <span className="inline">
            {words.map((w, i) => (
                <motion.span
                    key={`${w}-${i}`}
                    initial={{ opacity: 0, y: 40, rotate: -2 }}
                    whileInView={{ opacity: 1, y: 0, rotate: 0 }}
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{
                        duration: 0.9,
                        delay: delay + i * 0.08,
                        ease: [0.19, 1, 0.22, 1],
                    }}
                    className="inline-block mr-3 last:mr-0"
                >
                    {w}
                </motion.span>
            ))}
            {trailing}
        </span>
    );
}

// Big "01"/"02"/"03" with odometer-style count-up when the card is in view.
function CountedNumber({ target, inView }) {
    const [val, setVal] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const controls = animate(0, target, {
            duration: 1.1,
            ease: [0.19, 1, 0.22, 1],
            onUpdate(v) {
                setVal(v);
            },
        });
        return () => controls.stop();
    }, [inView, target]);

    const display = String(Math.round(val)).padStart(2, "0");
    return (
        <span className="font-display block text-[5.5rem] leading-[0.85] text-[#EB4A18] tabular-nums tracking-tight">
            {display}
        </span>
    );
}
